import {
    WorkspaceMemberWithUser,
    WorkspaceWithDetail,
    WorkspaceWithInfo,
} from '@/common/types/databaseTypes';
import { createBaseWorkspaceService } from '@tickitacka/shared';

const baseWorkspaceService = createBaseWorkspaceService();

export const workspaceService = {
    createWorkspace: (
        name: string,
        description?: string,
        profileImageUrl?: string
    ) =>
        baseWorkspaceService.createWorkspace(
            name,
            description,
            profileImageUrl
        ),

    getWorkspaceById: (workspaceId: string): Promise<WorkspaceWithInfo> =>
        baseWorkspaceService.getWorkspaceById(workspaceId),

    getMyWorkspaces: (): Promise<WorkspaceWithInfo[]> =>
        baseWorkspaceService.getMyWorkspaces(),

    getWorkspaceDetailsById: (
        workspaceId: string
    ): Promise<WorkspaceWithDetail> =>
        baseWorkspaceService.getWorkspaceDetailsById(workspaceId),

    updateWorkspace: (
        id: string,
        name?: string,
        description?: string,
        profileImageUrl?: string
    ) =>
        baseWorkspaceService.updateWorkspace(
            id,
            name,
            description,
            profileImageUrl
        ),

    deleteWorkspace: (workspaceId: string): Promise<string> =>
        baseWorkspaceService.deleteWorkspace(workspaceId),

    transferWorkspaceOwnership: (
        workspaceId: string,
        oldOwnerId: string,
        newOwnerId: string
    ): Promise<boolean> =>
        baseWorkspaceService.transferWorkspaceOwnership(
            workspaceId,
            oldOwnerId,
            newOwnerId
        ),

    getWorkspaceMembers: (
        workspaceId: string
    ): Promise<WorkspaceMemberWithUser[]> =>
        baseWorkspaceService.getWorkspaceMembers(workspaceId),

    addWorkspaceMember: (
        workspaceId: string,
        email: string,
        role = 'member',
        registerId?: string
    ) =>
        baseWorkspaceService.addWorkspaceMember(
            workspaceId,
            email,
            role,
            registerId
        ),

    removeWorkspaceMember: (
        workspaceId: string,
        userId: string
    ): Promise<boolean> =>
        baseWorkspaceService.removeWorkspaceMember(workspaceId, userId),

    updateWorkspaceMemberRole: (
        workspaceId: string,
        userId: string,
        role: string
    ): Promise<boolean> =>
        baseWorkspaceService.updateWorkspaceMemberRole(
            workspaceId,
            userId,
            role
        ),

    /**
     * 팀 ID를 통해 해당 팀이 속한 워크스페이스의 멤버인지 확인합니다.
     * @param teamId 확인할 팀 ID
     * @param userId 확인할 사용자 ID
     * @returns 워크스페이스 멤버십 여부와 관련 정보를 담은 객체
     */
    checkUserWorkspaceMembership: (
        teamId: string,
        userId: string
    ): Promise<{
        isAllowed: boolean;
        workspaceId?: string;
        error?: string;
    }> => baseWorkspaceService.checkUserWorkspaceMembership(teamId, userId),

    /**
     * 목표 ID를 통해 해당 목표가 속한 팀의 워크스페이스 정보를 조회합니다.
     * @param goalId 조회할 목표 ID
     * @returns 워크스페이스 정보
     */
    getWorkspaceByGoalId: (goalId: string): Promise<WorkspaceWithInfo | null> =>
        baseWorkspaceService.getWorkspaceByGoalId(goalId),

    /**
     * 목표 ID와 사용자 ID를 통해 해당 사용자가 목표가 속한 워크스페이스의 멤버인지 확인합니다.
     * @param goalId 확인할 목표 ID
     * @param userId 확인할 사용자 ID
     * @returns 워크스페이스 멤버십 여부(boolean)
     */
    checkWorkspaceMembershipByGoalId: (
        goalId: string,
        userId: string
    ): Promise<boolean> =>
        baseWorkspaceService.checkWorkspaceMembershipByGoalId(goalId, userId),

    /**
     * 두 사용자가 같은 워크스페이스의 멤버인지 확인합니다.
     * @param userId1 첫 번째 사용자 ID
     * @param userId2 두 번째 사용자 ID
     * @returns 같은 워크스페이스 멤버인지 여부와 관련 정보를 담은 객체
     */
    checkSameWorkspaceMembership: (
        userId1: string,
        userId2: string
    ): Promise<{
        isSameWorkspace: boolean;
        workspaceIds?: string[];
        error?: string;
    }> => baseWorkspaceService.checkSameWorkspaceMembership(userId1, userId2),
};
